import {Button, Link} from '@primer/react'
import type {SearchResponse, UserResult as UserResultType, UsersResults} from '../../types/blackbird-types'
import React from 'react'
import {reactFetch} from '@github-ui/verified-fetch'
import Result from '../search-result'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {Count} from '@github-ui/count'
import {PeopleIcon, RepoIcon} from '@primer/octicons-react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {clsx} from 'clsx'
import styles from './Users.module.css'

const SPONSOR_BUTTONS_PATH = '/sponsors/batch_deferred_sponsor_buttons'

function Users({results}: {results: UsersResults & SearchResponse}) {
  const [sponsorButtonMap, setSponsorButtonMap] = React.useState<{[key: string]: SafeHTMLString}>()

  React.useEffect(() => {
    async function populateSponsorButtonMap(formData: FormData) {
      const sponsorResponse = await reactFetch(SPONSOR_BUTTONS_PATH, {body: formData, method: 'POST'})

      if (sponsorResponse.ok) {
        const sponsorButtons = await sponsorResponse.json()

        setSponsorButtonMap(sponsorButtons)
      }
    }

    const sponsorFormData = new FormData()

    for (const result of results.results) {
      const formRepoKey = `items[item-${result.id}]`

      if (result.sponsorable && !sponsorFormData.has(`${formRepoKey}[sponsorable_id]`)) {
        if (!sponsorFormData.has('_method')) {
          sponsorFormData.set('_method', 'GET')
        }

        sponsorFormData.set(`${formRepoKey}[sponsorable_id]`, result.id)
        sponsorFormData.set(`${formRepoKey}[sponsorable_login]`, result.login)
        sponsorFormData.set(`${formRepoKey}[repo_name]`, '')
        sponsorFormData.set(`${formRepoKey}[has_funding_file]`, 'false')
        sponsorFormData.set(`${formRepoKey}[location]`, 'USER_SEARCH_RESULT_SPONSOR')
      }
    }

    if (sponsorFormData.has('_method')) {
      populateSponsorButtonMap(sponsorFormData)
    }
  }, [results])

  return (
    <Result.List>
      {results.results.map((item, i) => {
        let sponsorButtonHtml: SafeHTMLString | undefined
        if (sponsorButtonMap) {
          sponsorButtonHtml = sponsorButtonMap[`item-${item.id}`]
        }

        return (
          <UserResult
            item={item}
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={i}
            sponsorButtonHtml={sponsorButtonHtml}
            signInPath={!results.logged_in ? results.sign_in_path : ''}
          />
        )
      })}
    </Result.List>
  )
}

function UserResult({
  item,
  sponsorButtonHtml,
  signInPath,
}: {
  item: UserResultType
  sponsorButtonHtml?: SafeHTMLString
  signInPath?: string
}) {
  return (
    <Result>
      <div className={styles.resultRow}>
        <div className={styles.leftColumn}>
          <div className={styles.avatarWrapper}>
            <GitHubAvatar src={item.development_avatar || item.avatar_url} size={40} className={styles.avatar} />
          </div>
          <div className={styles.contentColumn}>
            <Result.Header>
              <Result.Title>
                <Title user={item} />
              </Result.Title>
            </Result.Header>

            {item.hl_profile_bio && (
              <Result.Content>
                <span>
                  <Result.SearchMatchText text={item.hl_profile_bio} />
                </span>
              </Result.Content>
            )}

            {item.location && (
              <Result.Footer>
                <Result.FooterItem>
                  <span>{item.location}</span>
                </Result.FooterItem>

                <Result.FooterItem>
                  <RepoIcon size={16} className={styles.footerIcon} />
                  <Count ariaLabel={`${item.repos} repositories`} value={item.repos} />
                </Result.FooterItem>

                <Result.FooterItem>
                  <PeopleIcon size={16} className={styles.footerIcon} />
                  <Count ariaLabel={`${item.followers} followers`} value={item.followers} />
                </Result.FooterItem>
              </Result.Footer>
            )}
          </div>
        </div>
        <div className={styles.rightColumn}>
          {!item.is_current_user && (
            <div>
              <FollowButton followedByUser={item.followed_by_current_user} login={item.login} signInPath={signInPath} />
            </div>
          )}
          {sponsorButtonHtml && <VerifiedHTMLDiv html={sponsorButtonHtml} className={styles.sponsorButton} />}
        </div>
      </div>
    </Result>
  )
}

function Title({user}: {user: UserResultType}) {
  const userHref = `/${user.display_login}`
  return (
    <>
      {user.hl_name && (
        <Link href={userHref}>
          <span className={styles.titleName}>{user.name}</span>
        </Link>
      )}
      <Link href={userHref}>
        <span className={clsx(styles.titleLogin, user.name && styles.titleLoginSpaced)}>
          <span className={styles.titleLoginText}>{user.display_login}</span>
        </span>
      </Link>
    </>
  )
}

function FollowButton({
  followedByUser,
  login,
  signInPath,
}: {
  followedByUser: boolean
  login: string
  signInPath?: string
}) {
  const [followed, setFollowed] = React.useState(followedByUser)
  const actionPath = `/users/${followed ? 'unfollow' : 'follow'}?target=${login}`

  const onToggle = React.useCallback(async () => {
    setFollowed(!followed)
    const res = await reactFetch(actionPath, {method: 'POST'})
    if (!res.ok) {
      // following failed, so revert back to the previous state
      setFollowed(followed)
    }
  }, [actionPath, followed])

  return signInPath ? (
    <Button size="small" as="a" href={signInPath}>
      {followed ? 'Unfollow' : 'Follow'}
    </Button>
  ) : (
    <Button onClick={onToggle} size="small">
      {followed ? 'Unfollow' : 'Follow'}
    </Button>
  )
}

export default Users
